import React, { useState, useEffect, useRef, useCallback } from "react";
import * as Mui from "@mui/material";
import { useFetch } from "services/hooks/useFetch";
import {
  themes,
  scrollTop,
  tableRowSize,
  drawerStyle,
  stickyHeader,
} from "services/constants";
import { useSelector } from "react-redux";
import { InfoMessage } from "components/CustomComponents/CustomButton";
import { showToast } from "services/utils/Status";
import { checkEditorPermission } from "services/utils/HelperFunction";
import { Loader } from "components/Loader";
import { customGetAPI } from "services/utils/CustomAPI";
import { CustomFilter } from "components/CustomComponents/CustomFilter";
import { Typography, Box, Paper, Drawer } from "@mui/material";
import { PersonAddAlt, Workspaces, ArrowRight } from "@mui/icons-material";
import { CompanyProgress } from "components/CustomComponents/CustomSlider";
import comLogo from "../../../../assets/img/brand/company_logo.png";
import { CreateEmployeeGroup } from "./CreateEmployeeGroup";
import { ViewEmployeeGroup } from "./ViewEmployeeGroup";
import { CustomButton } from "components/CustomComponents";

const AssignEmployeeGroupHeader = [
  {
    name: "",
    key: "expand",
  },
  {
    key: "group_name",
    name: "Name",
    type: "string",
    icon: <Workspaces fontSize="small" />,
  },
  {
    name: "No of Employees",
    key: "no_of_employees",
    type: "number",
    icon: <PersonAddAlt fontSize="small" />,
  },
];

export const IndividualEmployeeGroupDetails = [
  {
    name: "Employee ID",
    key: "employee_id",
  },
  {
    name: "Employee Name",
    key: "employee_name",
  },
  {
    name: "Department",
    key: "department",
  },
  {
    name: "Designation",
    key: "designation",
  },
  {
    name: "Effective From",
    key: "effective_from",
  },
  {
    name: "Effective To",
    key: "effective_to",
  },
];

export const EmployeeGroup = () => {
  const { domain, userScreenAccess, topbarCompanyId } = useSelector(
    (state) => state.tokenReducer
  );

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [employeeGroupDetails, setEmployeeGroupDetails] = useState([]);
  const [selectedId, setSelectedId] = useState(0);
  const [individualLoader, setIndividualLoader] = useState(false);
  const [expandDetails, setExapandDetils] = useState([]);
  const [expandItem, setExpandItem] = useState([]);
  const [triggerApi, setTriggerApi] = useState(false);
  const containerRef = useRef(null);
  const [isCreatePanelOpen, setIsCreatePanelOpen] = useState(false);
  const [isViewPanelOpen, setIsViewPanelOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [handleLoader, setHandleLoader] = useState(false);
  const [expandedIndex, setExpandedIndex] = useState(null);
  const [expandedData, setExpandedData] = useState("");

  let check_access = checkEditorPermission(
    userScreenAccess?.module_details,
    "Employee",
    "Employee"
  );

  const {
    result: employeeGroupData,
    setLoad: reCallApi,
    loading,
  } = useFetch(`${domain}employee-group/`);

  const handleCreateOpen = () => setIsCreatePanelOpen(true);
  const handleCreateClose = () => setIsCreatePanelOpen(false);

  const handleViewOpen = (item) => {
    if (!item) return;
    setSelectedItem(item);
    setSelectedId(item?.group_id);
    setIsViewPanelOpen(true);
  };

  const handleViewClose = () => setIsViewPanelOpen(false);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleToggle = (index, value) => {
    setExpandedIndex((prev) => (prev === index ? null : index));
    setExpandedData(value);
  };

  useEffect(() => {
    scrollTop(containerRef);
  }, [page]);

  useEffect(() => {
    if (employeeGroupData?.length !== 0 && handleLoader) {
      const item = employeeGroupData.find((d) => d.group_id === selectedId);
      if (item) setSelectedItem(item);
    }
  }, [selectedId, handleLoader, employeeGroupData]);

  useEffect(() => {
    const fetchIndividualGroupDetails = async () => {
      setIndividualLoader(true);
      const res = await customGetAPI(
        `${domain}employee-group/?group_id=${expandItem?.group_id}`
      );
      if (res.ok) {
        setExapandDetils(res?.responseDetails?.employees);
        setTriggerApi(false);
        setIndividualLoader(false);
      } else {
        setTriggerApi(false);
        setIndividualLoader(false);
      }
    };
    if (triggerApi) {
      fetchIndividualGroupDetails();
    }
  }, [domain, expandItem?.group_id, triggerApi]);

  const handleFilteredDataChange = useCallback(
    (filteredData) => {
      setEmployeeGroupDetails(filteredData);
      if (page * rowsPerPage >= filteredData.length) setPage(0);
    },
    [page, rowsPerPage]
  );

  const NewEmpGrpButton = (
    <CustomButton
      actionFuntion={() => {
        if (check_access) {
          handleCreateOpen();
        } else {
          showToast(
            "error",
            "Access denied: You are not authorized to create employee group"
          );
        }
      }}
      actionName="New"
      typeName="button"
    />
  );

  useEffect(() => {
    if (topbarCompanyId) {
      reCallApi(true);
      setHandleLoader(false);
    }
  }, [reCallApi, topbarCompanyId]);

  return expandedData && loading ? (
    <Loader />
  ) : (
    <React.Fragment>
      <Box>
        <Box
          sx={{
            mb: 2,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
              Employee Group
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Assign employees to group with defined rules
            </Typography>
          </Box>
        </Box>

        <Paper sx={{ width: "100%", height: "78vh" }}>
          <CustomFilter
            title="Employee Group Records"
            filterHeaders={AssignEmployeeGroupHeader}
            data={employeeGroupData}
            filteredData={employeeGroupDetails}
            onFilteredDataChange={handleFilteredDataChange}
            screenAccess={check_access}
            newButtonComponent={NewEmpGrpButton}
          />

          <Mui.Grid sx={{ ml: 2, mr: 2 }}>
            <Mui.TableContainer ref={containerRef} sx={{ maxHeight: 590 }}>
              <Mui.Table className="border">
                <Mui.TableHead sx={stickyHeader}>
                  <Mui.TableRow sx={tableRowSize}>
                    {AssignEmployeeGroupHeader?.map((value) => (
                      <Mui.TableCell
                        sx={{
                          color: themes.headLine,
                          fontWeight: "bold",
                        }}
                      >
                        {value?.name}
                      </Mui.TableCell>
                    ))}
                  </Mui.TableRow>
                </Mui.TableHead>
                <Mui.TableBody>
                  {employeeGroupDetails?.length === 0 ? (
                    <InfoMessage dataLength={employeeGroupData} />
                  ) : (
                    employeeGroupDetails
                      ?.slice(
                        page * rowsPerPage,
                        page * rowsPerPage + rowsPerPage
                      )
                      ?.map((value, index) => (
                        <React.Fragment key={index}>
                          <Mui.TableRow sx={tableRowSize}>
                            {AssignEmployeeGroupHeader.map((head) => (
                              <Mui.TableCell
                                key={head.key}
                                onClick={() => {
                                  if (head.key === "expand") {
                                    handleToggle(index, value);
                                    setExapandDetils([]);
                                    setExpandItem(value);
                                    setTriggerApi(true);
                                  } else {
                                    handleViewOpen(value);
                                    setExpandedIndex(null);
                                  }
                                }}
                              >
                                {head.key === "expand" ? (
                                  <ArrowRight
                                    sx={{
                                      transition: "0.3s",
                                      transform:
                                        expandedIndex === index
                                          ? "rotate(90deg)"
                                          : "rotate(0deg)",
                                      cursor: "pointer",
                                    }}
                                  />
                                ) : (
                                  value[head.key] ?? "N/A"
                                )}
                              </Mui.TableCell>
                            ))}
                          </Mui.TableRow>

                          {expandedIndex === index && (
                            <Mui.TableRow>
                              <Mui.TableCell colSpan={12}>
                                <Mui.TableContainer
                                  sx={{
                                    marginTop: 0,
                                    marginLeft: "auto",
                                    marginRight: "auto",
                                    width: "100%px",
                                  }}
                                  className="w-96 border rounded"
                                >
                                  <Mui.Table>
                                    <Mui.TableHead
                                      sx={{
                                        backgroundColor: themes.primary,
                                      }}
                                    >
                                      <Mui.TableRow
                                        sx={{
                                          "& .MuiTableCell-root": {
                                            height: "6px",
                                            padding: "6px 10px",
                                          },
                                        }}
                                      >
                                        {IndividualEmployeeGroupDetails.map(
                                          (head) => (
                                            <Mui.TableCell
                                              sx={{
                                                color: themes.headLine,
                                                fontWeight: "bold",
                                              }}
                                            >
                                              {head.name}
                                            </Mui.TableCell>
                                          )
                                        )}
                                      </Mui.TableRow>
                                    </Mui.TableHead>
                                    <Mui.TableBody>
                                      {individualLoader ? (
                                        <Mui.TableRow sx={tableRowSize}>
                                          <Mui.TableCell
                                            colSpan={10}
                                            style={{
                                              textAlign: "center",
                                            }}
                                          >
                                            <CompanyProgress
                                              comLogo={comLogo}
                                              themes={themes}
                                            />
                                          </Mui.TableCell>
                                        </Mui.TableRow>
                                      ) : expandDetails?.length === 0 &&
                                        !individualLoader ? (
                                        <Mui.TableRow
                                          maxWidth="xl"
                                          align="left"
                                          sx={tableRowSize}
                                        >
                                          <Mui.TableCell colSpan={12}>
                                            <Mui.Alert
                                              sx={{ marginTop: 3 }}
                                              severity="info"
                                            >
                                              No data available
                                            </Mui.Alert>
                                          </Mui.TableCell>
                                        </Mui.TableRow>
                                      ) : (
                                        expandDetails?.map((detail) => (
                                          <Mui.TableRow
                                            sx={{
                                              "& .MuiTableCell-root": {
                                                height: "10px",
                                                padding: "10px 10px",
                                              },
                                            }}
                                          >
                                            {IndividualEmployeeGroupDetails?.map(
                                              (head) => (
                                                <Mui.TableCell key={head.key}>
                                                  {detail[head.key] ||
                                                  detail[head.key] === 0
                                                    ? detail[head.key]
                                                    : "N/A"}
                                                </Mui.TableCell>
                                              )
                                            )}
                                          </Mui.TableRow>
                                        ))
                                      )}
                                    </Mui.TableBody>
                                  </Mui.Table>
                                </Mui.TableContainer>
                              </Mui.TableCell>
                            </Mui.TableRow>
                          )}
                        </React.Fragment>
                      ))
                  )}
                </Mui.TableBody>
              </Mui.Table>
            </Mui.TableContainer>
            <Box sx={{ mt: 1 }}>
              {employeeGroupDetails?.length > 25 && (
                <Mui.TablePagination
                  className="custom-pagination"
                  component="div"
                  rowsPerPageOptions={[25, 50, 75, 100]}
                  count={employeeGroupDetails?.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                />
              )}
            </Box>
          </Mui.Grid>
        </Paper>
      </Box>

      <Drawer
        anchor="right"
        open={isCreatePanelOpen || isViewPanelOpen}
        onClose={() => {
          isCreatePanelOpen ? handleCreateClose() : handleViewClose();
        }}
        sx={drawerStyle}
      >
        {isCreatePanelOpen ? (
          <CreateEmployeeGroup
            onClose={handleCreateClose}
            reCallApi={reCallApi}
            setHandleLoader={setHandleLoader}
          />
        ) : isViewPanelOpen ? (
          <ViewEmployeeGroup
            onClose={handleViewClose}
            editEmpGrpDetails={selectedItem}
            reCallApi={reCallApi}
            setGetId={setSelectedId}
            setIsViewPanelOpen={(v) => setIsViewPanelOpen(v)}
            setHandleLoader={setHandleLoader}
            screenAccess={check_access}
          />
        ) : null}
      </Drawer>
    </React.Fragment>
  );
};
